import Header from "@/components/header/header";
import PspButton from "@/components/pspButton/PspButton";
import { translate, TranslatorKeys } from "@/config/translator";
import { useAttendanceDesktop } from "./attendanceDesktop/useAttendanceDesktop";
import { AttendanceTable } from "./attendanceDesktop/attendanceTable";
import { Dispatch, SetStateAction } from "react";
import { AttendanceDesktopVociDrawer } from "./attendanceDesktop/attendanceDesktopVociDrawer";

import { AttendanceDesktopVersionsDrawer } from "./attendanceDesktop/attendanceDesktopVersionsDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faCloudArrowUp,
  faCloudCheck,
} from "@fortawesome/pro-regular-svg-icons";
import { faPaperPlaneTop } from "@fortawesome/pro-solid-svg-icons";
import ConfirmationDialog from "@/components/confirmationDialog/ConfirmationDialog";
import PspSelect from "@/components/pspSelect/pspSelect";
import Skeleton from "react-loading-skeleton";
import { clsx } from "clsx/lite";
import { formatDateFromLang } from "@/config/utils";
import { MessageBar } from "@fluentui/react-components";

type AttendanceDesktopProps = {
  setRestoreOriginalConfirmationOpen: Dispatch<SetStateAction<boolean>>;
  openSubmitAttendanceConfirmation: () => void;
  onRestoreEmployee: (employeeMatricola: number) => void;
  canRestoreOriginal: boolean;
};

export function AttendanceDesktop({
  setRestoreOriginalConfirmationOpen,
  openSubmitAttendanceConfirmation,
  onRestoreEmployee,
  canRestoreOriginal,
}: AttendanceDesktopProps) {
  const {
    lang,
    onSaveEdit,
    vociOpenEmployee,
    selectedMonth,
    selectedYear,
    openVoci,
    closeVoci,
    vociOpen,
    versionsOpen,
    setVersionsOpen,
    tempAttendanceMonth,
    setTempAttendanceMonth,
    restoreEmployeeConfirmationOpen,
    wantsToRestoreEmployee,
    openRestoreEmployeeDialog,
    closeRestoreEmployeeDialog,
    restoreEmployeeWrapper,
    availableMonths,
    onSelectMonth,
    isSaving,
    isSubmitted,
    submittedAt,
    canRevokeAttendance,
    onRevokeAttendance,
  } = useAttendanceDesktop({ onRestoreEmployee });

  return (
    <div className="p-10">
      <Header
        rightSide={
          <div className="flex flex-row gap-3 items-center">
            {isSubmitted && submittedAt ? (
              canRevokeAttendance ? (
                <PspButton onClick={onRevokeAttendance}>
                  {translate("revokeAttendance", lang)}
                </PspButton>
              ) : (
                <div>
                  <MessageBar>
                    <div className="pr-3">
                      <span>{translate("alreadySubmittedOn", lang)} </span>
                      <span className="font-semibold">
                        {formatDateFromLang(submittedAt, lang)}
                      </span>
                    </div>
                  </MessageBar>
                </div>
              )
            ) : (
              <>
                <FontAwesomeIcon
                  icon={isSaving ? faCloudArrowUp : faCloudCheck}
                  className={clsx(
                    "duration-200",
                    "ease-in-out",
                    isSaving
                      ? "text-2xl text-primary pr-2"
                      : "text-lg text-font-secondary pr-3",
                    "px-3",
                  )}
                />
                <PspButton
                  icon={
                    <FontAwesomeIcon
                      className="text-lg"
                      icon={faClockRotateLeft}
                    />
                  }
                  size="large"
                  appearance="secondary"
                  onClick={() => setVersionsOpen(true)}
                />
                {canRestoreOriginal && (
                  <PspButton
                    appearance="secondary"
                    size="large"
                    onClick={() => setRestoreOriginalConfirmationOpen(true)}
                  >
                    {translate("restoreOriginal", lang)}
                  </PspButton>
                )}
                <PspButton
                  size="large"
                  appearance="primary"
                  onClick={() => onSaveEdit(false)}
                >
                  {translate("saveDraft", lang)}
                </PspButton>
                <PspButton
                  onClick={openSubmitAttendanceConfirmation}
                  size="large"
                  appearance="primary"
                >
                  <div className="flex flex-row gap-2 items-center">
                    <div>{translate("submitAttendance", lang)}</div>
                    <FontAwesomeIcon
                      className="text-font-white"
                      icon={faPaperPlaneTop}
                    />
                  </div>
                </PspButton>
              </>
            )}
          </div>
        }
      >
        {translate("attendances", lang)}
      </Header>
      <div className="-mt-6 pb-10 flex flex-row justify-start w-full">
        <div className="w-60">
          {availableMonths ? (
            <PspSelect
              onChange={onSelectMonth}
              value={`${selectedMonth} ${selectedYear}`}
            >
              {availableMonths.map((m) => (
                <option value={`${m.month} ${m.year}`}>
                  {translate(`month${m.month - 1}` as TranslatorKeys, lang)}{" "}
                  {m.year}
                </option>
              ))}
            </PspSelect>
          ) : (
            <Skeleton width={300} height={50} />
          )}
        </div>
      </div>
      <AttendanceTable
        onSaveEdit={onSaveEdit}
        openVoci={openVoci}
        tempAttendance={tempAttendanceMonth}
        openRestoreEmployeeDialog={openRestoreEmployeeDialog}
        isSaving={isSaving}
      />
      <AttendanceDesktopVersionsDrawer
        onClose={() => setVersionsOpen(false)}
        setTempAttendance={setTempAttendanceMonth}
        isOpen={versionsOpen}
      />
      <AttendanceDesktopVociDrawer
        isOpen={vociOpen}
        onClose={closeVoci}
        employee={vociOpenEmployee}
      />
      <ConfirmationDialog
        isOpen={restoreEmployeeConfirmationOpen}
        titleString={translate("discardAllChangesAttendancesTitle", lang)}
        messageString={translate("discardAllChangesAttendancesBody", lang, {
          "{employeeName}": wantsToRestoreEmployee?.name ?? "",
        })}
        onCancel={closeRestoreEmployeeDialog}
        onConfirm={() =>
          wantsToRestoreEmployee
            ? restoreEmployeeWrapper(wantsToRestoreEmployee.matricola)
            : {}
        }
      />
    </div>
  );
}
